import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  Autocomplete,
  CircularProgress,
  IconButton,
  Container,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { extractList } from "../utils/response";
import {
  useCreateTeamMutation,
  useLazyGetTeamByIdQuery,
  useUpdateTeamMutation,
  useLazyGetUserByMultiRolesQuery,
  useLazyGetUsersByRoleQuery,
  useLazyGetUserStatusQuery,
} from "../store/api";
import { TEAMS_LISTS_PATH } from "../navigations/path";
import {
  getErrorMessage,
  getValidationErrorDetails,
} from "../utils/errorHandler";

/** User/app roles from API member or user object (array of { name } or strings). */
const formatRolesDisplay = (entity) => {
  if (!entity) return "";
  const raw = entity.roles;
  if (Array.isArray(raw) && raw.length) {
    return raw
      .map((r) => (typeof r === "string" ? r : r?.name))
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

const userOptionLabel = (u) => {
  if (!u) return "";
  const roles = formatRolesDisplay(u);
  return roles ? `${u.name || ""} (${roles})` : u.name || "";
};

const CreateTeamForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [createTeam] = useCreateTeamMutation();
  const [updateTeam] = useUpdateTeamMutation();
  const [fetchTeamById] = useLazyGetTeamByIdQuery();
  const [fetchUserByMultiRoles] = useLazyGetUserByMultiRolesQuery();
  const [fetchUsersByRole] = useLazyGetUsersByRoleQuery();
  const [fetchUserStatus] = useLazyGetUserStatusQuery();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode"); // 'edit' | 'view' | null
  const teamId = queryParams.get("id");

  // Form State
  const [teamName, setTeamName] = useState("");
  const [teamLead, setTeamLead] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [status, setStatus] = useState("");

  // Data
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const statusesLoadedRef = useRef(false);
  const leadsLoadedRef = useRef(false);
  const usersLoadedRef = useRef(false);

  // UI State
  const [isLoading, setIsLoading] = useState(
    Boolean(teamId && (mode === "edit" || mode === "view")),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(mode === "view");

  // Errors
  const [errors, setErrors] = useState({
    teamName: "",
    teamLead: "",
    status: "",
  });

  const loadStatuses = useCallback(async () => {
    if (statusesLoadedRef.current || statusesLoading) return;
    setStatusesLoading(true);
    try {
      const statusRes = await fetchUserStatus().unwrap();
      const statusList = Array.isArray(statusRes)
        ? statusRes
        : statusRes?.list || [];
      setStatuses(statusList);
      statusesLoadedRef.current = true;
    } catch (err) {
      toast.error(
        err?.data?.message || getErrorMessage(err) || "Failed to load statuses",
      );
    } finally {
      setStatusesLoading(false);
    }
  }, [fetchUserStatus, statusesLoading]);

  const loadLeads = useCallback(async () => {
    if (leadsLoadedRef.current || leadsLoading) return;
    setLeadsLoading(true);
    try {
      const leadsRes = await fetchUsersByRole("Sales Lead").unwrap();
      const leadsList = extractList(leadsRes?.data ?? leadsRes ?? []);
      setLeads((prev) => {
        const normalized = leadsList.map((u) => ({
          ...u,
          uuid: u.uuid || u.id,
        }));
        const byId = new Map(normalized.map((u) => [u.id ?? u.uuid, u]));
        prev.forEach((u) => {
          const key = u.id ?? u.uuid;
          if (!byId.has(key)) byId.set(key, u);
        });
        return Array.from(byId.values());
      });
      leadsLoadedRef.current = true;
    } catch (err) {
      toast.error(
        err?.data?.message ||
          getErrorMessage(err) ||
          "Failed to load team leads",
      );
    } finally {
      setLeadsLoading(false);
    }
  }, [fetchUsersByRole, leadsLoading]);

  const loadUsers = useCallback(async () => {
    if (usersLoadedRef.current || usersLoading) return;
    setUsersLoading(true);
    try {
      const usersRes = await fetchUserByMultiRoles({
        "roles[]": ["Closer", "Lead Gen"],
        paginate: 0,
      }).unwrap();
      const usersList = extractList(usersRes?.data ?? usersRes ?? []);
      setUsers((prev) => {
        const normalized = usersList.map((u) => ({
          ...u,
          uuid: u.uuid || u.id,
        }));
        const byId = new Map(normalized.map((u) => [u.id ?? u.uuid, u]));
        prev.forEach((u) => {
          const key = u.id ?? u.uuid;
          if (!byId.has(key)) byId.set(key, u);
        });
        return Array.from(byId.values());
      });
      usersLoadedRef.current = true;
    } catch (err) {
      toast.error(
        err?.data?.message || getErrorMessage(err) || "Failed to load users",
      );
    } finally {
      setUsersLoading(false);
    }
  }, [fetchUserByMultiRoles, usersLoading]);

  // Edit/view: only load the team — select options load when user opens each field
  useEffect(() => {
    if (!(teamId && (mode === "edit" || mode === "view"))) {
      setIsLoading(false);
      return;
    }

    const loadTeam = async () => {
      setIsLoading(true);
      try {
        const teamRes = await fetchTeamById(teamId).unwrap();
        const team = teamRes?.data ?? teamRes;
        if (!team) throw new Error("Team not found");

        setTeamName(team.name || "");
        setStatus(team.status?.id || "");

        const leadMember = team.members?.find((m) => m.role === "lead");
        if (leadMember) {
          const lead = {
            id: leadMember.id,
            uuid: leadMember.id,
            name: leadMember.name,
            email: leadMember.email,
            roles: Array.isArray(leadMember.roles) ? leadMember.roles : [],
          };
          setLeads((prev) =>
            prev.some((l) => l.id === lead.id || l.uuid === lead.id)
              ? prev
              : [...prev, lead],
          );
          setTeamLead(lead);
        }

        const members = (team.members || [])
          .filter((m) => m.role === "member")
          .map((m) => ({
            id: m.id,
            uuid: m.id,
            name: m.name,
            email: m.email,
            roles: Array.isArray(m.roles) ? m.roles : [],
          }));
        if (members.length) {
          setUsers((prev) => {
            const byId = new Map(prev.map((u) => [u.id ?? u.uuid, u]));
            members.forEach((m) => {
              if (!byId.has(m.id)) byId.set(m.id, m);
            });
            return Array.from(byId.values());
          });
          setSelectedUsers(members);
        }
      } catch (err) {
        toast.error(
          err?.data?.message || getErrorMessage(err) || "Failed to load team",
        );
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTeam();
  }, [teamId, mode, fetchTeamById]);

  // === VALIDATION & SUBMIT ===
  const validate = () => {
    const newErrors = { teamName: "", teamLead: "", status: "" };

    if (!teamName.trim()) newErrors.teamName = "Team Name is required";
    if (!teamLead) newErrors.teamLead = "Team Lead is required";
    if (!status) newErrors.status = "Status is required";

    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix all errors");
      return;
    }

    setIsSubmitting(true);
    setIsFormDisabled(true);

    const payload = {
      name: teamName.trim(),
      status_id: status,
      members: [
        { id: teamLead.uuid || teamLead.id, role: "lead" },
        ...selectedUsers.map((u) => ({ id: u.uuid || u.id, role: "member" })),
      ],
    };

    try {
      let res;
      if (mode === "edit" && teamId) {
        res = await updateTeam({ teamId, teamData: payload }).unwrap();
      } else {
        res = await createTeam(payload).unwrap();
      }

      if (res?.data ?? res) {
        toast.success(
          `Team "${teamName}" ${mode === "edit" ? "updated" : "added"}!`,
        );
        navigate(TEAMS_LISTS_PATH);
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      const { message, errors: serverErrors } = getValidationErrorDetails(err);
      toast.error(message || "Operation failed");
      if (Object.keys(serverErrors).length > 0) {
        // API keys -> form fields (name -> teamName, members[] -> teamLead)
        const { name, status_id: statusId, ...rest } = serverErrors;
        const memberError = Object.entries(rest).find(([k]) =>
          k.startsWith("members"),
        );
        setErrors((prev) => ({
          ...prev,
          ...(name ? { teamName: name } : {}),
          ...(statusId ? { status: statusId } : {}),
          ...(memberError ? { teamLead: memberError[1] } : {}),
        }));
      }
    } finally {
      setIsSubmitting(false);
      setIsFormDisabled(mode === "view");
    }
  };

  const handleBack = () => navigate(TEAMS_LISTS_PATH);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress color="primary" />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ flexGrow: 1, p: 4, minHeight: "100vh" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            color={"primary.main"}
          >
            {mode === "edit"
              ? "Edit Team"
              : mode === "view"
                ? "View Team"
                : "Add Team"}
          </Typography>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            maxWidth: 900,
            mx: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Team Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Team Name"
                  value={teamName}
                  onChange={(e) => {
                    setTeamName(e.target.value);
                    if (e.target.value.trim())
                      setErrors((prev) => ({ ...prev, teamName: "" }));
                  }}
                  error={!!errors.teamName}
                  helperText={errors.teamName}
                  disabled={isFormDisabled}
                  placeholder="e.g., Development Squad A"
                />
              </Grid>

              {/* Team Lead */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={leads}
                  getOptionLabel={userOptionLabel}
                  value={teamLead}
                  onOpen={loadLeads}
                  loading={leadsLoading}
                  onChange={(e, v) => {
                    setTeamLead(v);
                    if (v) setErrors((prev) => ({ ...prev, teamLead: "" }));
                  }}
                  isOptionEqualToValue={(o, v) =>
                    o.id === v.id || o.uuid === v.uuid
                  }
                  disabled={isFormDisabled}
                  noOptionsText={leadsLoading ? "Loading…" : "No leads found"}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id ?? option.uuid}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          py: 0.25,
                        }}
                      >
                        <Typography variant="body2">{option.name}</Typography>
                        {formatRolesDisplay(option) ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatRolesDisplay(option)}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team Lead"
                      error={!!errors.teamLead}
                      helperText={errors.teamLead}
                    />
                  )}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={!!errors.status}
                  disabled={isFormDisabled}
                >
                  <InputLabel>Team Status</InputLabel>
                  <Select
                    value={status}
                    label="Team Status"
                    onOpen={loadStatuses}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      if (e.target.value)
                        setErrors((prev) => ({ ...prev, status: "" }));
                    }}
                  >
                    {statusesLoading && statuses.length === 0 ? (
                      <MenuItem disabled>Loading…</MenuItem>
                    ) : statuses.length > 0 ? (
                      statuses.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No statuses</MenuItem>
                    )}
                  </Select>
                  {errors.status && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ ml: 1.5, mt: 0.5 }}
                    >
                      {errors.status}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Team Members */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={userOptionLabel}
                  value={selectedUsers}
                  onOpen={loadUsers}
                  loading={usersLoading}
                  onChange={(e, v) => setSelectedUsers(v)}
                  isOptionEqualToValue={(o, v) =>
                    o.id === v.id || o.uuid === v.uuid
                  }
                  disableCloseOnSelect
                  disabled={isFormDisabled}
                  noOptionsText={usersLoading ? "Loading…" : "No users found"}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id ?? option.uuid}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          py: 0.25,
                        }}
                      >
                        <Typography variant="body2">{option.name}</Typography>
                        {formatRolesDisplay(option) ? (
                          <Typography variant="caption" color="text.secondary">
                            {formatRolesDisplay(option)}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team Members (Optional)"
                      placeholder={
                        selectedUsers.length === 0 ? "Search users..." : ""
                      }
                    />
                  )}
                  renderTags={(tags, getTagProps) =>
                    tags.map((opt, i) => (
                      <Chip
                        key={opt.uuid || opt.id || i}
                        label={
                          formatRolesDisplay(opt)
                            ? `${opt.name} (${formatRolesDisplay(opt)})`
                            : opt.name || ""
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                        {...getTagProps({ index: i })}
                      />
                    ))
                  }
                />
              </Grid>

              {/* Submit */}
              {mode !== "view" && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting}
                    sx={{ py: 1.5, borderRadius: 2 }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} />
                    ) : mode === "edit" ? (
                      "Update Team"
                    ) : (
                      "Add Team"
                    )}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateTeamForm;
